import {
  Card,
  CardHeader,
  makeStyles,
  SkeletonItem,
  Text,
} from '@fluentui/react-components';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  getValidationIconColor,
  ValidationTestCaseResultIcon,
} from './ValidationTestCaseResultIcon';
import { ValidationResults } from '../abstractions/EAppValidation';
import { IAppValidationItem } from '../abstractions/IAppValidation';

const useStyles = makeStyles({
  card: {
    width: '380px',
    height: '150px',
    boxSizing: 'border-box',
    borderRadius: '4px',
  },
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '18px',
    height: '100vh',
  },
  element: {
    flex: '0 1 auto',
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
});

export const ValidationInfo: React.FC<{
  currentValidation: IAppValidationItem | null;
}> = ({ currentValidation }) => {
  const classes = useStyles();
  const intl = useIntl();

  const header = intl.formatMessage({
    defaultMessage: 'Info',
    description: 'Validation Info Title',
  });

  if (!currentValidation) {
    return <CardSkeleton />;
  }

  return (
    <Card className={classes.card}>
      <CardHeader header={<Text weight="semibold">{header}</Text>} />
      <ValidationInfoInner currentValidation={currentValidation} />
    </Card>
  );
};

export const ValidationInfoInner: React.FC<{
  currentValidation: IAppValidationItem;
}> = ({ currentValidation }) => {
  const classes = useStyles();
  const intl = useIntl();

  const successText = intl.formatMessage({
    defaultMessage: 'Success',
    description: 'Validation Success Text',
  });
  const warningText = intl.formatMessage({
    defaultMessage: 'Warning',
    description: 'Validation Warning Text',
  });
  const errorText = intl.formatMessage({
    defaultMessage: 'Error',
    description: 'Validation Error Text',
  });
  const skippedText = intl.formatMessage({
    defaultMessage: 'Skipped',
    description: 'Validation Skipped Text',
  });

  const success = currentValidation.validationResults?.successes?.length ?? 0;
  const warnings = currentValidation.validationResults?.warnings?.length ?? 0;
  const failures = currentValidation.validationResults?.failures?.length ?? 0;
  const skipped = currentValidation.validationResults?.skipped?.length ?? 0;

  return (
    <div className={classes.wrapper}>
      <Element
        count={success}
        result={ValidationResults.Success}
        text={successText}
      />
      <Element
        count={warnings}
        result={ValidationResults.Warning}
        text={warningText}
      />
      <Element
        count={failures}
        result={ValidationResults.Error}
        text={errorText}
      />
      <Element
        count={skipped}
        result={ValidationResults.Skipped}
        text={skippedText}
      />
    </div>
  );
};

const CardSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <Card className={classes.card}>
      <CardHeader header={<SkeletonItem style={{ width: '30%' }} />} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <SkeletonItem style={{ width: '80%' }} />
        <SkeletonItem style={{ width: '50%' }} />
        <SkeletonItem style={{ width: '30%' }} />
      </div>
    </Card>
  );
};

const Element: React.FC<{
  count: number;
  result: ValidationResults;
  text: string;
}> = ({ count, result, text }) => {
  const classes = useStyles();
  return (
    <div className={classes.element}>
      <Text
        weight="semibold"
        size={800}
        style={{ color: getValidationIconColor(result) }}
      >
        {count}
      </Text>
      <div style={{ gap: '2px', alignItems: 'center', display: 'flex' }}>
        <ValidationTestCaseResultIcon result={result} />
        <Text>{text}</Text>
      </div>
    </div>
  );
};
